import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { PricingSection } from '@/components/PricingSection';
import { ProcessSlider } from '@/components/ProcessSlider';
import { TestimonialSlider } from '@/components/TestimonialSlider';
import { ServicesSlider } from '@/components/ServicesSlider';
import { AttorneysSection } from '@/components/AttorneysSection';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

export default function Home({ onOpenPopup }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <div className="min-h-screen" data-testid="home-page">
      <Helmet>
        <title>Trademark Law Consulting - Professional Trademark Registration Services in USA</title>
        <meta name="description" content="Protect your brand with comprehensive trademark registration, copyright registration, and legal services. Fast, affordable, and expert trademark filing starting at $45. Trusted by thousands of businesses across the USA." />
        <meta name="keywords" content="trademark registration, copyright registration, trademark search, brand protection, USPTO filing, trademark attorney, intellectual property" />
      </Helmet>
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/8112131/pexels-photo-8112131.jpeg"
            alt="Hero background"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-[#0A1C3E] opacity-90" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
                Trusted Trademark Solutions
              </span>
              <h1 className="text-5xl md:text-7xl font-heading font-bold text-white leading-tight mb-6">
                Safeguard Your Brand with {' '}
                <span className="text-[#E1A334]">
                  Assurance
                </span>
              </h1>
              <p className="text-lg md:text-xl text-slate-300 leading-relaxed mb-8 max-w-2xl">
                Full trademark filing and legal support services created to protect your business identity throughout the United States.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={onOpenPopup}
                  data-testid="hero-get-started-button"
                  className="bg-[#E1A334] hover:bg-[#C4912E] text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl hover:shadow-[#E1A334]/30 transition-all duration-300 hover:-translate-y-1"
                >
                  Register My Trademark
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button
                  onClick={() => document.getElementById('services').scrollIntoView({ behavior: 'smooth' })}
                  variant="outline"
                  data-testid="hero-explore-button"
                  className="border-slate-200 text-white hover:bg-white hover:text-slate-900 rounded-full px-8 py-6 text-lg font-medium transition-all duration-300"
                >
                  Explore Services
                </Button>
              </div>
            </motion.div>

            <motion.div
              className="lg:col-span-5"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 bg-[#E1A334] rounded-3xl blur-3xl opacity-20" />
                <img
                  src="/assets/img/home-header.webp"
                  alt="Professional business meeting"
                  className="relative rounded-3xl shadow-2xl border border-slate-200 w-full h-auto"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Info Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="/assets/img/about-sec.webp"
                alt="Team collaboration"
                className="rounded-3xl shadow-xl border border-slate-200 w-full h-auto"
              />
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
                Why Work With Us
              </span>
              <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-6">
                Your Reliable Partner in Brand Security
              </h2>
              <p className="text-slate-600 text-lg leading-relaxed mb-6">
                At Trademark Law Consulting, we know your brand is not just a title—it represents your reputation, identity, and long-term success. Our skilled professionals deliver complete trademark and legal solutions designed to protect what is most valuable to your business.
              </p>
              <p className="text-slate-600 text-lg leading-relaxed mb-8">
                With extensive experience and thousands of successful filings, we have assisted companies across the United States in securing intellectual property and strengthening brand value for the future.
              </p>
              <Button
                onClick={onOpenPopup}
                data-testid="info-section-button"
                className="bg-[#E1A334] hover:bg-[#C4912E] text-white font-semibold rounded-full px-8 py-4 shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                Begin Your Registration
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
              Our Offerings
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Full Brand Protection Services
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              From trademark filing to complete legal support, we provide everything your business needs
            </p>
          </motion.div>

          <ServicesSlider onOpenPopup={onOpenPopup} />
        </div>
      </section>

      {/* Attorneys Section */}
      <AttorneysSection />

      {/* Pricing Section */}
      <PricingSection onOpenPopup={onOpenPopup} />

      {/* Process Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
              Our Method
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Fast, Easy, and Secure
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              We have simplified the trademark registration journey into three straightforward steps
            </p>
          </motion.div>

          <ProcessSlider />
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-sm font-medium tracking-widest uppercase text-[#E1A334] mb-4 block">
              Client Reviews
            </span>
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-slate-900 mb-4">
              Trusted by Companies Nationwide
            </h2>
            <p className="text-slate-600 text-lg max-w-2xl mx-auto">
              See feedback from our valued clients
            </p>
          </motion.div>

          <TestimonialSlider />
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 md:py-20 bg-[#0A1C3E] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-white/10" />
        </div>
        
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-5xl font-heading font-bold text-white mb-6">
              Final Step to Protect Your Brand
            </h2>
            <p className="text-[#E1A334]/80 text-lg md:text-xl max-w-2xl mx-auto mb-8">
              Join thousands of businesses who rely on us for trademark filing. Start today and secure your brand’s future.
            </p>
            <Button
              onClick={onOpenPopup}
              data-testid="cta-section-button"
              className="bg-white text-[#0A1C3E] hover:bg-[#E1A334] hover:text-white font-semibold rounded-full px-8 py-6 text-lg shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              Start Your Application Now
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
